































































import { Component, Prop, Watch } from 'vue-property-decorator';
import GiganautVue from '@/components/Shared/GiganautVue';
import ajax from '@/lib/ajax';
import EmailAttachmentDto from '@/models/Activities/EmailAttachmentDto';
import { AttachmentUploadUrlResponse } from '@/models/Activities/EmailDraftRequest';

interface LocalAttachment extends EmailAttachmentDto {
    uploading?: boolean;
    file?: File;
}

const MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024; // 25MB per file
const MAX_TOTAL_SIZE = 50 * 1024 * 1024; // 50MB total

@Component({
    name: 'EmailAttachmentList'
})
export default class EmailAttachmentList extends GiganautVue {
    @Prop({ default: '' })
    activityId!: string;

    @Prop({ default: () => [] })
    value!: EmailAttachmentDto[];

    @Prop({ default: false })
    disabled!: boolean;

    error = '';
    pendingFiles: File[] = [];

    get attachments(): LocalAttachment[] {
        return this.value as LocalAttachment[];
    }

    triggerFileInput() {
        (this.$refs.fileInput as HTMLInputElement).click();
    }

    async onFilesSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const files = input.files;
        if (!files || files.length === 0) return;

        this.error = '';

        // If no draft exists yet, buffer the files and ask the parent to create one
        if (!this.activityId) {
            this.pendingFiles = Array.from(files);
            input.value = '';
            this.$emit('ensure-draft');
            return;
        }

        await this.processFiles(Array.from(files));
        input.value = '';
    }

    async processFiles(files: File[]) {
        for (const file of files) {
            // Validate size
            if (file.size > MAX_ATTACHMENT_SIZE) {
                this.error = `File "${file.name}" exceeds maximum size of 25MB`;
                continue;
            }

            // Check total size
            const currentTotal = this.attachments.reduce((sum, att) => sum + att.sizeBytes, 0);
            if (currentTotal + file.size > MAX_TOTAL_SIZE) {
                this.error = 'Total attachments size exceeds 50MB limit';
                break;
            }

            await this.uploadFile(file);
        }
    }

    @Watch('activityId')
    async onActivityIdChanged(newVal: string) {
        if (newVal && this.pendingFiles.length > 0) {
            const files = this.pendingFiles;
            this.pendingFiles = [];
            await this.processFiles(files);
        }
    }

    async uploadFile(file: File) {
        // Add a temporary local attachment
        const tempAttachment: LocalAttachment = {
            id: 'temp-' + Date.now(),
            agencyId: '',
            activityId: this.activityId,
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            sizeBytes: file.size,
            storagePath: '',
            providerAttachmentId: '',
            createdAt: new Date(),
            createdById: '',
            uploading: true,
            file: file
        };

        const newAttachments = [...this.attachments, tempAttachment];
        this.$emit('input', newAttachments);

        try {
            // 1. Get signed upload URL
            const urlResponse = await ajax.post(
                `/api/v1/agency/activities/emails/${this.activityId}/attachments/upload-url`,
                {
                    filename: file.name,
                    contentType: file.type || 'application/octet-stream'
                }
            );
            const uploadInfo: AttachmentUploadUrlResponse = urlResponse.data;

            // 2. Upload to GCS
            await fetch(uploadInfo.signedUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type || 'application/octet-stream'
                }
            });

            // 3. Confirm upload
            const confirmResponse = await ajax.post(
                `/api/v1/agency/activities/emails/${this.activityId}/attachments/confirm`,
                {
                    attachmentId: uploadInfo.attachmentId,
                    filename: file.name,
                    contentType: file.type || 'application/octet-stream',
                    sizeBytes: file.size,
                    storagePath: uploadInfo.storagePath
                }
            );

            // Update the attachment with the confirmed data
            const confirmedAttachment = confirmResponse.data;
            const index = this.attachments.findIndex(a => a.id === tempAttachment.id);
            if (index > -1) {
                const updated = [...this.attachments];
                updated[index] = {
                    ...confirmedAttachment,
                    uploading: false
                };
                this.$emit('input', updated);
            }

            // Emit updated activity timestamp (attachment ops bump updated_at)
            if (confirmedAttachment.activityUpdatedAt) {
                this.$emit('activity-updated', confirmedAttachment.activityUpdatedAt);
            }
        } catch (error: any) {
            // Remove failed attachment
            const index = this.attachments.findIndex(a => a.id === tempAttachment.id);
            if (index > -1) {
                const updated = [...this.attachments];
                updated.splice(index, 1);
                this.$emit('input', updated);
            }
            this.error = 'Failed to upload attachment: ' + (error.response?.data?.message || error.message);
        }
    }

    async removeAttachment(attachment: LocalAttachment, index: number) {
        if (attachment.uploading) return;

        // If it has a real ID (not temp), delete from server
        if (attachment.id && !attachment.id.startsWith('temp-')) {
            try {
                const response = await ajax.delete(`/api/v1/agency/activities/emails/attachments/${attachment.id}`);
                if (response.data && response.data.activityUpdatedAt) {
                    this.$emit('activity-updated', response.data.activityUpdatedAt);
                }
            } catch (error) {
                // Continue to remove locally even if server delete fails
            }
        }

        const updated = [...this.attachments];
        updated.splice(index, 1);
        this.$emit('input', updated);
    }

    formatFileSize(bytes: number): string {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    getFileIcon(contentType: string): string {
        if (contentType.startsWith('image/')) return 'mdi-file-image';
        if (contentType.includes('pdf')) return 'mdi-file-pdf-box';
        if (contentType.includes('word') || contentType.includes('document')) return 'mdi-file-word';
        if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'mdi-file-excel';
        if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'mdi-file-powerpoint';
        if (contentType.startsWith('text/')) return 'mdi-file-document';
        if (contentType.includes('zip') || contentType.includes('archive')) return 'mdi-zip-box';
        return 'mdi-file';
    }

    getFileIconColor(contentType: string): string {
        if (contentType.startsWith('image/')) return 'purple';
        if (contentType.includes('pdf')) return 'red';
        if (contentType.includes('word') || contentType.includes('document')) return 'blue';
        if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'green';
        if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'orange';
        return 'grey';
    }
}
